if (!customElements.get('wls-video')) {
  class WLSVideo extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        playButton: 'js-wls-video-play-button',
        playing: 'playing',
      };

      this.observer = new IntersectionObserver(this._handleIntersection.bind(this), {
        threshold: 0.5,
      });

      this.boundVideoPlayingHandler = this._videoPlayingHandler.bind(this);
      this.boundHandleVideoEnded = this._handleVideoEnded.bind(this);
      this.boundHandlePlaying = this._handlePlaying.bind(this);

      // Bound handlers for native video events (when controls are present)
      this.boundHandleNativePlay = null;
      this.boundHandleNativePause = null;
    }

    /**
     * Get DOM elements
     * @returns {object} - Object containing DOM element references
     */
    get elements() {
      return {
        videos: this.querySelectorAll('video'),
        playButton: this.querySelector(`.${this.classes.playButton}`),
      };
    }

    /**
     * Get the currently visible video element
     * @returns {HTMLVideoElement|undefined} - The visible video element
     */
    getVisibleVideo() {
      return Array.from(this.elements.videos).find((video) => getComputedStyle(video).display !== 'none');
    }

    /**
     * Connected callback lifecycle method
     * @returns {void}
     */
    connectedCallback() {
      const video = this.getVisibleVideo();
      if (video) {
        const hasControls = video.hasAttribute('controls');
        this.observer.observe(this);

        // Add ended event handler to reset state
        video.addEventListener('ended', this.boundHandleVideoEnded);

        // If video has native controls, let browser handle play/pause
        // We just listen to native events to update our CSS classes
        if (hasControls) {
          // Create and store bound handlers
          this.boundHandleNativePlay = () => {
            this.classList.add(this.classes.playing);
          };

          this.boundHandleNativePause = () => {
            this.classList.remove(this.classes.playing);
          };

          // Listen to native play/pause events
          video.addEventListener('play', this.boundHandleNativePlay);
          video.addEventListener('pause', this.boundHandleNativePause);
        } else {
          // No native controls - use our custom click handler
          video.addEventListener('click', this.boundVideoPlayingHandler);
        }

        // Handle autoplay videos
        if (video.hasAttribute('autoplay')) {
          this._handleAutoplayVideo();
        } else if (this.elements.playButton) {
          this.elements.playButton.addEventListener('click', this.boundVideoPlayingHandler);
        }
      }
    }

    /**
     * Disconnected callback lifecycle method
     * @returns {void}
     */
    disconnectedCallback() {
      const video = this.getVisibleVideo();
      if (video) {
        video.removeEventListener('ended', this.boundHandleVideoEnded);
        video.removeEventListener('playing', this.boundHandlePlaying);
        video.removeEventListener('click', this.boundVideoPlayingHandler);

        // Remove native event listeners if they were added
        if (this.boundHandleNativePlay) {
          video.removeEventListener('play', this.boundHandleNativePlay);
        }
        if (this.boundHandleNativePause) {
          video.removeEventListener('pause', this.boundHandleNativePause);
        }
      }
      if (this.elements.playButton) {
        this.elements.playButton.removeEventListener('click', this.boundVideoPlayingHandler);
      }
      this.observer.disconnect();
    }

    /**
     * Handle autoplay video setup
     * @returns {void}
     */
    _handleAutoplayVideo() {
      if (this.elements.playButton) {
        this.elements.playButton.style.pointerEvents = 'none';
      }
      const video = this.getVisibleVideo();
      if (video) {
        video.addEventListener('playing', this.boundHandlePlaying);
      }
    }

    /**
     * Handle video playing event
     * @returns {void}
     */
    _handlePlaying() {
      this.classList.add(this.classes.playing);
    }

    /**
     * Handle intersection observer callback
     * @param {Array<IntersectionObserverEntry>} entries - Intersection observer entries
     * @returns {void}
     */
    _handleIntersection(entries) {
      entries.forEach((entry) => {
        const video = this.getVisibleVideo();
        if (video && !entry.isIntersecting && !video.paused) {
          this._videoPause();
        }
      });
    }

    /**
     * Pause all other synchronized videos
     * @returns {void}
     */
    _pauseSyncedVideos() {
      if (this.hasAttribute('data-sync-pause')) {
        const allSyncedVideos = document.querySelectorAll('wls-video[data-sync-pause]');
        allSyncedVideos.forEach((videoElement) => {
          if (videoElement !== this) {
            const v = videoElement.getVisibleVideo ? videoElement.getVisibleVideo() : null;
            if (v && !v.paused) {
              videoElement.classList.remove(this.classes.playing);
              v.pause();
            }
          }
        });
      }
    }

    /**
     * Handle video playing and pausing
     * @returns {void}
     */
    _videoPlayingHandler() {
      const video = this.getVisibleVideo();
      if (!video) return;
      if (video.paused) {
        this._pauseSyncedVideos();
        this.videoPlay();
      } else {
        this._videoPause();
      }
    }

    /**
     * Play the video (public method)
     * @returns {void}
     */
    videoPlay() {
      const video = this.getVisibleVideo();
      if (video) {
        video.play();
        this.classList.add(this.classes.playing);
      }
    }

    /**
     * Pause the video
     * @returns {void}
     */
    _videoPause() {
      const video = this.getVisibleVideo();
      if (video && !video.hasAttribute('autoplay')) {
        video.pause();
        this.classList.remove(this.classes.playing);
      }
    }

    /**
     * Handle video ended event, reset to initial state
     * @returns {void}
     */
    _handleVideoEnded() {
      this.classList.remove(this.classes.playing);
    }
  }

  customElements.define('wls-video', WLSVideo);
}
